<script setup>
import {ref, onMounted, nextTick} from 'vue'
import {
  PhPlus
} from "@phosphor-icons/vue";
import Modal from "~/components/common/modal.vue";
import SensorForm from "./form.vue";
import {useRoute} from "vue-router";
import {fetchSensors, deleteSensor} from "~/lib/api/machine_sensors";


let route = useRoute();
const orgUUID = route.params.organization_uuid;
const machineID = route.params.machine_id;
// Reactive state elements
const sensors = ref([])
const activeDevice = ref(null)
const activeFormSensor = ref(null)
const isDrawerOpen = ref(false)
const isModalOpen = ref(false)
const isEditing = ref(false)

nextTick(() => {
  // Initialize the modal state based on the URL hash
  //openFormModal()
})

// Fetch initial device array data from Rails index action
const _fetchSensors = async () => {
  try {
    const res = await fetchSensors(orgUUID, machineID);
    sensors.value = await res.data;
  } catch (err) {
    console.error('Failed fetching payload array:', err)
  }
}

// Load lifecycle initial hook
onMounted(_fetchSensors)

// Open Drawer Sidebar layout details
const openSidebar = (device) => {
  activeDevice.value = device
  isDrawerOpen.value = true
}

// Form visibility handler
const openFormModal = (device = null) => {
  activeFormSensor.value = device
  isModalOpen.value = true
}


// DELETE Request handling interface
const _deleteSensor = async (orgUUID, machineID, id) => {
  if (!confirm('Are you sure you want to remove this hardware node?')) return

  try {
    const res = await deleteSensor(orgUUID, machineID, id,)
    if (res.ok) {
      if (activeDevice.value?.id === id) isDrawerOpen.value = false
      await _fetchSensors()
    }
  } catch (err) {
    console.error('Failed routing deletion target hook:', err)
  }
}
</script>

<template>
  <div class="drawer drawer-end min-h-screen">
    <input id="status-sidebar" type="checkbox" class="drawer-toggle" :checked="isDrawerOpen"
           @change="isDrawerOpen = $event.target.checked"/>

    <!-- MAIN INTERFACE PAGE -->
    <div class="drawer-content  flex flex-col gap-6">

      <!-- Top Action Bar -->
      <div class="flex justify-between items-center dark:border-gray-700">
        <div>
          <h3>Machine Sensors</h3>
          <p class="text-sm opacity-60">Reports machine telemetry</p>
        </div>
        <button @click="openFormModal()" class="btn btn-sm gap-2">
          <PhPlus size="18" weight="bold"/>
          Add Sensor
        </button>
      </div>

      <!-- Sensor Card List Grid -->
      <div>
        <div
            v-for="device in sensors"
            :key="device.id"
            @click="openSidebar(device)"
            class="card bg-base-100 border border-base-100 shadow"
        >
          <div class="card-body dark:bg-gray-700 rounded-md">
            <div class="flex justify-between items-start">
              <div>
                <span class="badge badge-sm font-medium mb-1"
                      :class="device.sensor_type === 'Temperature' ? 'badge-primary' : 'badge-secondary'">
                  {{ device.sensor_type }}
                </span>
                <h2 class="card-title text-lg  transition-colors">{{ device.name }}</h2>
                <div class="text-sm text-base-200">
                  <div> SN : {{ device.sensor_sn }}</div>
                  <div> Hardware UUID : {{ device.hardware_uid }}</div>
                </div>
              </div>
              <span class="badge font-semibold"
                    :class="device.status === 'Online' ? 'badge-success text-success-content' : 'badge-ghost'">
                {{ device.status }}
              </span>
            </div>
            <div class="flex items-center gap-4 mt-4 text-sm opacity-70">
              <span class="flex items-center gap-1">📊 Last reading: <strong>{{ device.last_reading }}</strong></span>
            </div>

            <!-- Card Actions -->
            <div class="card-actions justify-end mt-4 pt-2 border-t border-base-300" @click.stop>
              <button @click="openFormModal(device)" class="btn border btn-ghost btn-sm  text-info">Edit</button>
              <button @click="_deleteSensor(device.id)" class="btn btn-sm btn-ghost text-error">Delete</button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SIDEBAR DRAWER OVERLAY & PANEL -->
    <div class="drawer-side z-50">
      <label for="status-sidebar" aria-label="close sidebar" class="drawer-overlay"></label>
      <div class="menu p-6 w-80 min-h-full bg-base-100 text-base-content flex flex-col justify-between">

        <div v-if="activeDevice">
          <div class="flex justify-between items-center mb-6 border-b pb-4">
            <h3 class="text-xl font-bold">Device Live Status</h3>
            <button @click="isDrawerOpen = false" class="btn btn-sm btn-circle btn-ghost">✕</button>
          </div>

          <div class="stats stats-vertical shadow w-full bg-base-200 gap-1 mb-4">
            <div class="stat">
              <div class="stat-title">Device Name</div>
              <div class="text-lg font-bold break-words">{{ activeDevice.name }}</div>
            </div>

            <div class="stat">
              <div class="stat-title">Current Metric Value</div>
              <div class="stat-value text-primary">{{ activeDevice.reading }}</div>
            </div>

            <div class="stat">
              <div class="stat-title">Network State</div>
              <div class="stat-value text-sm flex items-center gap-2 mt-1">
                <span class="badge" :class="activeDevice.status === 'Online' ? 'badge-success' : 'badge-error'"></span>
                {{ activeDevice.status }}
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-center opacity-50 py-10">No device active.</div>
      </div>
    </div>
  </div>

  <!-- CREATE / EDIT MODAL LAYER -->
  <dialog>
    <modal :is-open="isModalOpen" @close="isModalOpen = false">
      <template #header>
        <h3 class="font-bold text-lg ">{{ activeFormSensor ? 'Edit Sensor Config' : 'Register New Sensor' }}</h3>
      </template>
      <sensor-form :machineId="machineID?.toString()" :sensor="activeFormSensor" @refresh="_fetchSensors"
                   @close="isModalOpen = false"/>
    </modal>
  </dialog>

</template>
